import { apiFetch, apiFetchFormData } from "@/auth/apiClient";

export type ProfileResponse = {
  success: boolean;
  profile: {
    id?: number;
    firstName?: string;
    lastName?: string;
    contactNumber?: string;
    image?: string;
    user?: { id: number; email: string };
  } | null;
};

export type UserSettings = {
  id: number;
  emailNotifications: boolean;
  desktopNotifications: boolean;
  newConversationAlerts: boolean;
  language: string;
  timezone: string;
  darkMode: boolean;
};

export type SettingsResponse = {
  success: boolean;
  settings: UserSettings;
  isNewUser?: boolean;
};

export async function fetchProfile(): Promise<ProfileResponse> {
  return apiFetch<ProfileResponse>("/profile", { method: "GET" });
}

export async function saveProfile(body: {
  firstName?: string;
  lastName?: string;
  contactNumber?: string;
  image?: string;
}): Promise<ProfileResponse> {
  return apiFetch<ProfileResponse>("/profile", { method: "POST", json: body });
}

export type UploadProfilePictureResponse = {
  url: string;
  originalName: string;
  size: number;
  key?: string;
};

export async function uploadProfilePicture(file: File): Promise<UploadProfilePictureResponse> {
  const formData = new FormData();
  formData.append("file", file);
  return apiFetchFormData<UploadProfilePictureResponse>("/file-management/upload/profile-picture", formData);
}

export async function fetchSettings(): Promise<SettingsResponse> {
  return apiFetch<SettingsResponse>("/settings", { method: "GET" });
}

export async function updateSettings(body: Partial<{
  emailNotifications: boolean;
  desktopNotifications: boolean;
  newConversationAlerts: boolean;
  language: string;
  timezone: string;
  darkMode: boolean;
}>): Promise<SettingsResponse> {
  return apiFetch<SettingsResponse>("/settings", { method: "PUT", json: body });
}

export async function changePassword(body: {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}): Promise<{ success?: boolean; message?: string }> {
  return apiFetch("/auth/change_password", { method: "POST", json: body });
}

export function formatApiError(err: unknown): string {
  const e = err as { message?: string; body?: { message?: string | string[] } };
  const m = e?.body && typeof e.body === "object" && "message" in e.body ? (e.body as { message: string | string[] }).message : e?.message;
  if (Array.isArray(m)) return m.map(String).join(". ");
  if (typeof m === "string" && m.length) return m;
  return "Something went wrong";
}
